import { useRef, useCallback, useState, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { Icon } from './Icon'
import { useAgentStore } from '../../store'
import { useAimlapiTranscription } from '../../hooks/useAimlapiTranscription'

/* ─── Config ─── */
const LONG_PRESS_MS = 480

/* ─── Toast ─── */
let toastTimer: ReturnType<typeof setTimeout> | null = null

function showToast(msg: string) {
  const el = document.getElementById('app-toast')
  if (!el) return
  el.textContent = msg
  el.classList.add('show')
  if (toastTimer) clearTimeout(toastTimer)
  toastTimer = setTimeout(() => el.classList.remove('show'), 2200)
}

export function AppToast() {
  return <div id="app-toast" className="app-toast" role="status" />
}

/* ─── Voice Overlay ─────────────────────────────────────────────
 * Long-press-to-talk overlay aligned with the Gallery design
 * language: two-color background system (light/dark via theme tokens),
 * low corner radius, brand green primary, hairline dividers, no emoji.
 *
 * The overlay encodes Alive's core voice-routing semantic: when the
 * user long-presses the FAB and speaks, the message isn't going to
 * one specific agent — it's going to the user's *entire agent
 * network*, which then collectively decides who should accept it,
 * whether to spawn a conversation, pull a group together, or
 * dispatch as a task. To make that idea visible, the panel renders
 * a row of all the user's agent avatars at the top, with one agent
 * "lighting up" at a time as if the network is currently inspecting
 * who should take the request.
 *
 * Visual states:
 *   - normal:     green concentric rings + eq bars, agent row with
 *                 a rotating spotlight, "正在分发到 N 个 agent" hint
 *   - willCancel: red rings + red bars, "松开取消" hint, agent row
 *                 dims so the user sees that release will discard
 */
interface VoiceOverlayAgent {
  id:     string
  name:   string
  avatar?: string
}

interface VoiceOverlayProps {
  visible:    boolean
  willCancel: boolean
  elapsed:    number
  agents:     VoiceOverlayAgent[]
  /** Concatenated final transcript across all VAD-detected segments. */
  transcript: string
  /** How many audio segments are still being transcribed by aimlapi.
   * Used to render a "正在转录…" trailing indicator after the words
   * that have already arrived. */
  pendingSegments: number
  /** Whether the browser exposes the MediaRecorder + getUserMedia
   * APIs we need. */
  supported: boolean
  /** Whether the mic is currently capturing audio. */
  listening: boolean
  /** Most recent error string. null when no error. */
  error:     string | null
}

function VoiceOverlay({
  visible,
  willCancel,
  elapsed,
  agents,
  transcript,
  pendingSegments,
  supported,
  listening,
  error,
}: VoiceOverlayProps) {
  if (!visible) return null

  // 12 equalizer bars with deterministic pseudo-random heights that
  // re-roll on each elapsed-second tick so the bars feel "live".
  const bars = Array.from({ length: 12 }, (_, i) => {
    const seed = (i + 1) * 9301 + (elapsed + 1) * 49297
    const h = 30 + ((seed * 233280) % 100) % 70 // 30-100%
    return h
  })

  // Which agent is currently "lit up" — rotates one position per
  // second so the user sees the network actively considering each
  // agent. When the user is about to cancel we freeze it visually.
  const spotlightIndex = agents.length === 0 ? -1 : (elapsed % agents.length)

  const accent      = willCancel ? '#FF4D4F' : '#00D9A0'
  const accentSoft  = willCancel ? 'rgba(255,77,79,0.12)' : 'rgba(0,217,160,0.12)'
  const accentEdge  = willCancel ? 'rgba(255,77,79,0.35)' : 'rgba(0,217,160,0.35)'

  // Decide what to render in the subtitle bar above the panel.
  // Priority order:
  //   1. Browser doesn't support API at all → "浏览器不支持语音识别"
  //   2. Permission denied or other hard error → friendly explanation
  //   3. Recogniser is up but no text yet → "正在聆听…"
  //   4. Has transcript / interim → render them (final + interim)
  //
  // The subtitle bar is *always* rendered while the overlay is open
  // so the user sees state immediately, not only after speech arrives.
  let subtitleBody: React.ReactNode
  let subtitleVariant: 'normal' | 'muted' | 'error' = 'muted'

  if (!supported) {
    subtitleBody = '浏览器不支持麦克风录音'
    subtitleVariant = 'error'
  } else if (error === 'not-allowed') {
    subtitleBody = '请在浏览器顶部允许麦克风权限'
    subtitleVariant = 'error'
  } else if (error === 'audio-capture') {
    subtitleBody = '未检测到麦克风设备'
    subtitleVariant = 'error'
  } else if (transcript) {
    subtitleBody = (
      <>
        <span className="voice-subtitle-final">{transcript}</span>
        {pendingSegments > 0 && (
          <span className="voice-subtitle-interim"> 正在转录…</span>
        )}
      </>
    )
    subtitleVariant = 'normal'
  } else if (pendingSegments > 0) {
    subtitleBody = '正在转录…'
    subtitleVariant = 'muted'
  } else if (listening) {
    subtitleBody = '正在聆听…'
    subtitleVariant = 'muted'
  } else {
    subtitleBody = '准备就绪'
    subtitleVariant = 'muted'
  }

  return createPortal(
    <div
      className="voice-overlay"
      role="dialog"
      aria-label="Voice input"
    >
      {/* Live caption strip — floats above the panel like a video
          subtitle bar. Always visible while the overlay is open so
          the user sees the recogniser status (listening / error /
          unsupported) even before any text arrives. */}
      <div className={`voice-subtitle voice-subtitle-${subtitleVariant}`}>
        {subtitleBody}
      </div>

      <div className="voice-panel">
        {/* Agent row — every agent the user owns. Visually communicates
            that the voice command is broadcast to the whole network,
            not a single recipient. The currently spotlighted avatar
            gets a colored ring + scale-up so it reads as the network
            "looking at" that agent. */}
        {agents.length > 0 && (
          <div className="voice-agents" aria-hidden="true">
            {agents.slice(0, 8).map((a, i) => {
              const lit = !willCancel && i === spotlightIndex
              return (
                <div
                  key={a.id}
                  className={`voice-agent ${lit ? 'voice-agent-lit' : ''}`}
                  style={lit ? { borderColor: accent, boxShadow: `0 0 0 3px ${accentSoft}` } : undefined}
                  title={a.name}
                >
                  {a.avatar ? (
                    <img src={a.avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] font-bold" style={{ color: accent }}>
                      {a.name[0]?.toUpperCase()}
                    </span>
                  )}
                </div>
              )
            })}
            {agents.length > 8 && (
              <div className="voice-agent voice-agent-overflow">
                +{agents.length - 8}
              </div>
            )}
          </div>
        )}

        {/* Concentric pulsing rings — three layers for depth */}
        <div className="voice-rings">
          <span
            className="voice-ring-outer"
            style={{ background: accentSoft, borderColor: accentEdge }}
          />
          <span
            className="voice-ring-mid"
            style={{ background: accentSoft, borderColor: accentEdge }}
          />
          <span
            className="voice-ring-inner"
            style={{
              background: willCancel ? 'rgba(255,77,79,0.18)' : 'rgba(0,217,160,0.18)',
              borderColor: accent,
              color: accent,
            }}
          >
            <Icon
              name={willCancel ? 'close' : 'mic'}
              size={26}
              filled
              className="voice-ring-icon"
            />
          </span>
        </div>

        {/* Equalizer bars */}
        <div className="voice-eq" aria-hidden="true">
          {bars.map((h, i) => (
            <span
              key={i}
              className="voice-eq-bar"
              style={{
                height: `${h}%`,
                background: accent,
                animationDelay: `${i * 60}ms`,
              }}
            />
          ))}
        </div>

        {/* Elapsed time + hint */}
        <div className="voice-meta">
          <span className="voice-elapsed" style={{ color: accent }}>
            {String(Math.floor(elapsed / 60)).padStart(2, '0')}:{String(elapsed % 60).padStart(2, '0')}
          </span>
          <span className="voice-hint">
            {willCancel
              ? '松开取消'
              : agents.length > 0
                ? `分发到 ${agents.length} 位 agent · 松开发送 · 上滑取消`
                : '松开发送 · 上滑取消'}
          </span>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/* ─── Tab Config ─── */
interface TabItem {
  path: string
  icon: string
  label: string
}

const TABS: TabItem[] = [
  { path: '/', icon: 'dynamic_feed', label: 'nav.feed' },
  { path: '/conversations', icon: 'forum', label: 'nav.interactions' },
  { path: '/my-agent', icon: 'smart_toy', label: 'nav.myAgent' },
  { path: '/tasks', icon: 'task_alt', label: 'nav.tasks' },
  { path: '/profile', icon: 'badge', label: 'nav.portfolio' },
]

/* ─── Main TabBar ─── */
export function TabBar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  const myAgents = useAgentStore((s) => s.myAgents)
  // Map the user's owned agents into the slim shape the overlay
  // needs (id / name / avatar). Memoised so the overlay doesn't
  // re-render the avatar row on unrelated state changes.
  const overlayAgents = useMemo(
    () => myAgents.map((a) => ({ id: a.id, name: a.name, avatar: a.avatar })),
    [myAgents],
  )

  // Voice-activity-detected transcription via aimlapi (whisper-large).
  // The browser captures the mic with MediaRecorder, an analyser
  // node monitors RMS amplitude, and every time the user goes silent
  // for ~800ms we close the current segment and ship it to aimlapi
  // for transcription. Each natural sentence becomes its own request,
  // so the user sees subtitles arriving as soon as they pause for
  // breath instead of waiting for the entire recording to finish.
  // Web Speech API isn't usable in CN (Chrome routes through Google),
  // so this is the only path that works without a VPN.
  // i18n is unused here because whisper-large auto-detects language.
  void i18n
  const speech = useAimlapiTranscription()

  const [voiceActive, setVoiceActive] = useState(false)
  const [willCancel, setWillCancel] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const longPressRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startYRef = useRef<number | null>(null)
  const suppressClickRef = useRef(false)
  const agentBtnRef = useRef<HTMLButtonElement>(null)
  /** Set to true when the user releases the FAB but the last
   * audio segment hasn't been transcribed yet. The transcript
   * watcher effect will fire the navigation as soon as text
   * arrives. */
  const pendingCommitRef = useRef(false)
  /** Live mirror of `speech.transcript` so the pointerUp handler
   * can read the *current* value instead of a stale closure
   * snapshot. The handler closes over `speech` once at render
   * time, but transcript updates fire from the hook's async
   * onresult callback after the closure was captured — we'd see
   * an empty string in the closure even when state already has
   * the text. The ref is updated synchronously by the effect
   * below on every render. */
  const transcriptRef = useRef('')
  const pendingSegmentsRef = useRef(0)
  useEffect(() => {
    transcriptRef.current = speech.transcript
    pendingSegmentsRef.current = speech.pendingSegments
  })

  const isActive = useCallback(
    (path: string) =>
      path === '/'
        ? location.pathname === '/'
        : location.pathname.startsWith(path),
    [location.pathname],
  )

  /* ─── Long press handlers (my-agent tab only) ───────────────────
   * Lifecycle:
   *   1. pointerDown        → start LONG_PRESS_MS timer
   *   2. timer fires        → enter voice mode + start elapsed counter
   *   3. pointerMove        → if dragged > 60px upward, willCancel=true
   *                            (release will discard); back down clears it
   *   4. pointerUp/cancel   → tear down. willCancel=true → "cancelled"
   *                            toast; otherwise "sent" toast.
   */
  const SLIDE_CANCEL_PX = 60

  const teardownVoice = useCallback((resetTranscript: boolean) => {
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current)
      elapsedTimerRef.current = null
    }
    setVoiceActive(false)
    setWillCancel(false)
    setElapsed(0)
    startYRef.current = null
    // Only wipe the transcript on cancel paths or after a successful
    // navigation has already read it. Otherwise the watcher effect
    // (which fires when pendingSegments hits 0) needs the transcript
    // to still be in state when it runs.
    if (resetTranscript) {
      speech.reset()
    }
  }, [speech])

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return
    try { agentBtnRef.current?.setPointerCapture(e.pointerId) } catch {}
    startYRef.current = e.clientY
    longPressRef.current = setTimeout(() => {
      longPressRef.current = null
      setVoiceActive(true)
      setElapsed(0)
      elapsedTimerRef.current = setInterval(() => {
        setElapsed((n) => n + 1)
      }, 1000)
      if (navigator.vibrate) navigator.vibrate(12)
      // Kick off VAD transcription. start() is async because it
      // awaits getUserMedia. If the user previously denied mic
      // permission we proactively surface getUserMedia's native
      // prompt before retrying.
      if (speech.supported) {
        if (speech.error === 'not-allowed') {
          void speech.requestPermission().then((ok) => {
            if (ok) void speech.start()
          })
        } else {
          void speech.start()
        }
      }
    }, LONG_PRESS_MS)
  }, [speech])

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!voiceActive || startYRef.current == null) return
      const dy = startYRef.current - e.clientY
      const next = dy > SLIDE_CANCEL_PX
      setWillCancel((cur) => (cur === next ? cur : next))
    },
    [voiceActive],
  )

  const onPointerEnd = useCallback(
    (e: React.PointerEvent) => {
      if (longPressRef.current) {
        clearTimeout(longPressRef.current)
        longPressRef.current = null
      }
      if (voiceActive) {
        // Read latest transcript via ref — `speech` from props can
        // be a stale closure snapshot when async hook updates land
        // between render and pointerUp.
        const latestTranscript = transcriptRef.current
        const latestPending    = pendingSegmentsRef.current
        // eslint-disable-next-line no-console
        console.log('[voice] release', {
          willCancel,
          transcriptFromRef: latestTranscript,
          transcriptFromProp: speech.transcript,
          pendingSegmentsFromRef: latestPending,
          listening: speech.listening,
          error: speech.error,
        })
        if (willCancel) {
          if (speech.supported) speech.abort()
          showToast(t('voice.cancelled', '已取消'))
          teardownVoice(true)
        } else {
          if (speech.supported) speech.stop()
          const text = latestTranscript.trim()
          // eslint-disable-next-line no-console
          console.log('[voice] commit branch', { hasText: !!text, text, pendingSegments: latestPending })
          if (text) {
            showToast(t('voice.sentToTeam', '已发送给我和我的 Agents'))
            // Pass the recognized text via React Router's location
            // state. This survives Strict Mode's double-mount cleanly
            // (location.state is preserved across remounts), unlike
            // sessionStorage which can be raced or removed by the
            // first mount before the second pass reads it.
            navigate('/conversations/default-team', { state: { voiceText: text } })
            teardownVoice(true)
          } else if (latestPending > 0) {
            showToast(t('voice.transcribing', '正在转录…'))
            pendingCommitRef.current = true
            teardownVoice(false)
          } else {
            showToast(t('voice.empty', '没识别到内容'))
            teardownVoice(true)
          }
        }
        suppressClickRef.current = true
        e.preventDefault()
      }
      try { agentBtnRef.current?.releasePointerCapture(e.pointerId) } catch {}
    },
    [voiceActive, willCancel, speech, navigate, t, teardownVoice],
  )

  const onAgentClick = useCallback(() => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }
    navigate('/my-agent')
  }, [navigate])

  useEffect(() => {
    return () => {
      if (longPressRef.current) clearTimeout(longPressRef.current)
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current)
    }
  }, [])

  // If the recogniser fails with `not-allowed` while voice is
  // active, proactively trigger a getUserMedia permission prompt.
  // The user will see the browser's native permission UI; if they
  // grant it, the next long-press will succeed.
  useEffect(() => {
    if (!voiceActive) return
    if (speech.error === 'not-allowed') {
      void speech.requestPermission()
    }
  }, [voiceActive, speech])

  // Delayed commit: when the user released the FAB before any
  // segment had finished transcribing, pendingCommitRef stays true
  // and we wait here for transcript text to arrive. As soon as it
  // does, fire the navigation; if every in-flight segment came back
  // empty, surface "没识别到内容".
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[voice] watcher tick', {
      pendingCommit: pendingCommitRef.current,
      transcript: speech.transcript,
      pendingSegments: speech.pendingSegments,
    })
    if (!pendingCommitRef.current) return
    const text = speech.transcript.trim()
    if (text) {
      // eslint-disable-next-line no-console
      console.log('[voice] watcher COMMIT with text:', text)
      pendingCommitRef.current = false
      showToast(t('voice.sentToTeam', '已发送给我和我的 Agents'))
      navigate('/conversations/default-team', { state: { voiceText: text } })
      speech.reset()
      return
    }
    if (speech.pendingSegments === 0) {
      // eslint-disable-next-line no-console
      console.log('[voice] watcher EMPTY — declaring no content')
      pendingCommitRef.current = false
      showToast(t('voice.empty', '没识别到内容'))
      speech.reset()
    }
  }, [speech, navigate, t])

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-black/[0.06] dark:border-white/[0.08] md:hidden"
        style={{
          backgroundColor: 'var(--tabbar-bg, #FFFFFF)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        }}
      >
        <div className="px-2 py-2">
          <div className="flex justify-around items-center">
            {TABS.map((tab) => {
              const active = isActive(tab.path)
              const isCenter = tab.path === '/my-agent'

              // My Agent tab: enlarged icon in themed rounded-rect button, long-press for voice
              if (isCenter) {
                return (
                  <button
                    key={tab.path}
                    ref={agentBtnRef}
                    onClick={onAgentClick}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerEnd}
                    onPointerCancel={onPointerEnd}
                    className={`
                      flex items-center justify-center w-16 h-9 rounded
                      transition-transform
                      ${willCancel
                        ? 'bg-red-500 shadow-[0_0_0_4px_rgba(255,77,79,0.18),0_4px_16px_rgba(255,77,79,0.4)] scale-[1.08]'
                        : voiceActive
                          ? 'bg-primary scale-[1.08] shadow-[0_0_0_4px_rgba(0,217,160,0.18),0_4px_16px_rgba(0,217,160,0.4)]'
                          : 'bg-primary shadow-[0_2px_12px_rgba(0,217,160,0.3)]'
                      }
                    `}
                    style={{
                      touchAction: 'none',
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                    }}
                    aria-label="Agent"
                  >
                    <Icon
                      name={voiceActive ? (willCancel ? 'close' : 'mic') : tab.icon}
                      size={28}
                      filled
                      className={`text-white ${voiceActive ? 'animate-pulse' : ''}`}
                    />
                  </button>
                )
              }

              // Normal tabs
              return (
                <button
                  key={tab.path}
                  onClick={() => navigate(tab.path)}
                  className={`flex items-center flex-col gap-0.5 justify-center min-w-[48px] ${
                    active ? 'text-primary' : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  <Icon
                    name={tab.icon}
                    size={24}
                    filled={active}
                  />
                  <span className={`text-[10px] ${active ? 'font-semibold' : 'font-medium'}`}>
                    {t(tab.label)}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </nav>

      <VoiceOverlay
        visible={voiceActive}
        willCancel={willCancel}
        elapsed={elapsed}
        agents={overlayAgents}
        transcript={speech.transcript}
        pendingSegments={speech.pendingSegments}
        supported={speech.supported}
        listening={speech.listening}
        error={speech.error}
      />
    </>
  )
}
